import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Briefcase, 
  Users, 
  Phone, 
  TrendingUp, 
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  Star,
  ChevronRight,
  RefreshCw,
  Filter,
  Search
} from 'lucide-react';
import axiosInstance from '../../config/axiosConfig';
import {
  countActiveCandidates,
  countCallsToday,
  getRecentActiveJDs
} from '../../utils/dashboardData';

/**
 * DashboardMobile - Mobile-optimized dashboard for recruiters
 * 
 * Features:
 * - Responsive grid layout optimized for mobile screens
 * - Touch-friendly cards with 44px+ tap targets
 * - Real-time data updates with pull-to-refresh
 * - High contrast colors for mobile readability
 * - Lazy loading for performance
 * - Reduced motion support
 * - Dark mode compatibility
 * 
 * Generated by Windsurf Product Developer Intelligence — Task 31 Mobile UX Execution Continuity
 */

const DashboardMobile = ({ 
  userData = {},
  onRefresh,
  onViewJDs,
  onViewCandidates,
  onViewCalls,
  onViewSchedule,
  className = ''
}) => {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullStartY, setPullStartY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Real dashboard data - no mock data
  const [stats, setStats] = useState({
    totalJDs: null,
    activeCandidates: null,
    callsToday: null,
    interviewsScheduled: null,
  });

  const [recentJDs, setRecentJDs] = useState([]);
  const [dataErrors, setDataErrors] = useState({});
  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    setIsRefreshing(true);

    try {
      const [jdsResult, candidatesResult, callsResult] = await Promise.allSettled([
        axiosInstance.get('/jds/list?status_filter=Active'),
        axiosInstance.get('/candidates'),
        axiosInstance.get('/call_logs')
      ]);

      const errors = {};

      const readArrayResponse = (result, key, message) => {
        if (result.status === 'fulfilled' && Array.isArray(result.value.data)) {
          return result.value.data;
        }

        errors[key] = message;
        const reason = result.status === 'rejected'
          ? result.reason
          : new Error(`Unexpected ${key} response shape`);
        console.error(`[DashboardMobile] Failed to load ${key}:`, reason);
        return null;
      };

      const jds = readArrayResponse(
        jdsResult,
        'jobDescriptions',
        'Recent job descriptions could not be loaded.'
      );
      const candidates = readArrayResponse(
        candidatesResult,
        'candidates',
        'Candidate totals could not be loaded.'
      );
      const callLogs = readArrayResponse(
        callsResult,
        'callLogs',
        'Today\'s call total could not be loaded.'
      );

      setStats({
        totalJDs: jds === null ? null : jds.length,
        activeCandidates: candidates === null ? null : countActiveCandidates(candidates),
        callsToday: callLogs === null ? null : countCallsToday(callLogs),
        interviewsScheduled: null, // No interviews API is available yet.
      });

      setRecentJDs(jds === null ? [] : getRecentActiveJDs(jds));
      setDataErrors(errors);
      setRecentActivities([]); // TODO: Fetch from activities API when available
      setUpcomingInterviews([]); // TODO: Fetch from interviews API when available
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Initialize dashboard data on mount
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Handle pull-to-refresh
  const handleTouchStart = useCallback((e) => {
    if (window.scrollY === 0) {
      setPullStartY(e.touches[0].clientY);
      setIsPulling(true);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling) return;
    
    const currentY = e.touches[0].clientY;
    const distance = currentY - pullStartY;
    
    if (distance > 0) {
      setPullDistance(Math.min(distance, 120));
    }
  }, [isPulling, pullStartY]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;
    
    if (pullDistance > 80) {
      await loadDashboardData();
      onRefresh?.();
    }
    
    setIsPulling(false);
    setPullDistance(0);
  }, [isPulling, pullDistance, loadDashboardData, onRefresh]);

  // Handle stat card click
  const handleStatCardClick = useCallback((type) => {
    switch (type) {
      case 'jds':
        if (onViewJDs) onViewJDs();
        else navigate('/jd-upload');
        break;
      case 'candidates':
        if (onViewCandidates) onViewCandidates();
        else navigate('/candidates');
        break;
      case 'calls':
        onViewCalls?.();
        break;
      case 'interviews':
        onViewSchedule?.();
        break;
      default:
        break;
    }
  }, [navigate, onViewJDs, onViewCandidates, onViewCalls, onViewSchedule]);

  const displayStat = (value) => (value === null ? '—' : value);
  const describeStat = (value) => (value === null ? 'unavailable' : value);

  // Get activity icon
  const getActivityIcon = (type) => {
    const iconMap = {
      call: Phone,
      candidate: Users,
      jd: Briefcase,
      interview: Calendar,
    };
    return iconMap[type] || AlertCircle;
  };

  // Get activity color classes
  const getActivityColorClasses = (type, status) => {
    const colorMap = {
      call: {
        completed: 'text-success bg-success/10 dark:bg-success/20',
        missed: 'text-danger bg-danger/10 dark:bg-danger/20',
      },
      candidate: {
        new: 'text-primary bg-primary/10 dark:bg-primary/20',
        updated: 'text-warning bg-warning/10 dark:bg-warning/20',
      },
      jd: {
        updated: 'text-warning bg-warning/10 dark:bg-warning/20',
        created: 'text-success bg-success/10 dark:bg-success/20',
      },
      interview: {
        scheduled: 'text-primary bg-primary/10 dark:bg-primary/20',
        completed: 'text-success bg-success/10 dark:bg-success/20',
      },
    };
    return colorMap[type]?.[status] || colorMap[type]?.new || 'text-secondary bg-secondary/10 dark:bg-secondary/20';
  };

  return (
    <div 
      className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull-to-Refresh Indicator */}
      {isPulling && (
        <div 
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 transition-all duration-200"
          style={{ height: `${Math.min(pullDistance, 80)}px` }}
        >
          <RefreshCw 
            className={`w-5 h-5 text-primary ${isRefreshing ? 'animate-spin' : ''}`}
          />
        </div>
      )}

      {/* Header */}
      <div className="safe-area-top bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-mobile-xl font-bold text-gray-900 dark:text-white">
                Welcome back, {userData.name || 'Recruiter'}
              </h1>
              <p className="text-mobile-sm text-gray-600 dark:text-gray-400 mt-1">
                Here's your recruitment overview
              </p>
            </div>
            
            <button
              className="touch-target p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              onClick={async () => {
                await loadDashboardData();
                onRefresh?.();
              }}
              aria-label="Refresh dashboard"
            >
              <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search candidates, JDs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 text-mobile-sm bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              aria-label="Search"
            />
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center space-x-2 mt-3 overflow-x-auto scrollbar-hide">
            {['all', 'today', 'week', 'month'].map((filter) => (
              <button
                key={filter}
                className={`
                  touch-target px-4 py-2 text-mobile-sm font-medium rounded-lg whitespace-nowrap transition-colors
                  ${selectedFilter === filter
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }
                `}
                onClick={() => setSelectedFilter(filter)}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 space-y-6">
        {Object.keys(dataErrors).length > 0 && (
          <div
            className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
            role="alert"
          >
            <div className="flex items-start space-x-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-mobile-sm font-semibold">Some dashboard data is unavailable.</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-mobile-xs">
                  {Object.values(dataErrors).map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="mt-3 min-h-11 rounded-lg border border-red-400 px-4 py-2 text-mobile-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/50"
                  onClick={loadDashboardData}
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Total JDs */}
          <button
            className="touch-target-lg bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            onClick={() => handleStatCardClick('jds')}
            aria-label={`View ${describeStat(stats.totalJDs)} job descriptions`}
          >
            <div className="flex items-center justify-between mb-2">
              <Briefcase className="w-5 h-5 text-primary" />
              <TrendingUp className="w-4 h-4 text-success" />
            </div>
            <p className="text-mobile-2xl font-bold text-gray-900 dark:text-white">
              {displayStat(stats.totalJDs)}
            </p>
            <p className="text-mobile-xs text-gray-600 dark:text-gray-400">
              Total JDs
            </p>
          </button>

          {/* Active Candidates */}
          <button
            className="touch-target-lg bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            onClick={() => handleStatCardClick('candidates')}
            aria-label={`View ${describeStat(stats.activeCandidates)} active candidates`}
          >
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5 text-warning" />
              <TrendingUp className="w-4 h-4 text-success" />
            </div>
            <p className="text-mobile-2xl font-bold text-gray-900 dark:text-white">
              {displayStat(stats.activeCandidates)}
            </p>
            <p className="text-mobile-xs text-gray-600 dark:text-gray-400">
              Active Candidates
            </p>
          </button>

          {/* Calls Today */}
          <button
            className="touch-target-lg bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            onClick={() => handleStatCardClick('calls')}
            aria-label={`View ${describeStat(stats.callsToday)} calls today`}
          >
            <div className="flex items-center justify-between mb-2">
              <Phone className="w-5 h-5 text-success" />
              <Clock className="w-4 h-4 text-secondary" />
            </div>
            <p className="text-mobile-2xl font-bold text-gray-900 dark:text-white">
              {displayStat(stats.callsToday)}
            </p>
            <p className="text-mobile-xs text-gray-600 dark:text-gray-400">
              Calls Today
            </p>
          </button>

          {/* Interviews Scheduled */}
          <button
            className="touch-target-lg bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            onClick={() => handleStatCardClick('interviews')}
            aria-label="Scheduled interviews are not available"
          >
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-5 h-5 text-primary" />
              <AlertCircle className="w-4 h-4 text-warning" />
            </div>
            <p className="text-mobile-2xl font-bold text-gray-900 dark:text-white">
              {displayStat(stats.interviewsScheduled)}
            </p>
            <p className="text-mobile-xs text-gray-600 dark:text-gray-400">
              Interviews
            </p>
          </button>
        </div>

        {/* Recent Job Descriptions */}
        <section className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
            <h2 className="text-mobile-base font-semibold text-gray-900 dark:text-white">
              Recent Job Descriptions
            </h2>
            <Link
              to="/jd-upload"
              className="touch-target flex items-center text-mobile-sm font-medium text-primary hover:text-primary-dark"
            >
              View All
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </div>

          {dataErrors.jobDescriptions ? (
            <p className="p-4 text-mobile-sm text-red-700 dark:text-red-300">
              {dataErrors.jobDescriptions}
            </p>
          ) : recentJDs.length === 0 ? (
            <p className="p-4 text-mobile-sm text-gray-600 dark:text-gray-400">
              No active job descriptions yet.
            </p>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {recentJDs.map((jd) => (
                <Link
                  key={jd.id}
                  to={`/jd/${jd.id}`}
                  className="block min-h-11 p-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary dark:hover:bg-gray-800"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-mobile-sm font-semibold text-gray-900 dark:text-white">
                        {jd.role_title || 'Untitled role'}
                      </p>
                      <p className="mt-1 truncate text-mobile-xs text-gray-600 dark:text-gray-400">
                        {jd.client_name || 'Client not specified'}
                      </p>
                      {(jd.location || jd.experience_range) && (
                        <p className="mt-1 truncate text-mobile-xs text-gray-500 dark:text-gray-400">
                          {[jd.location, jd.experience_range].filter(Boolean).join(' • ')}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-gray-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Upcoming Interviews */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-mobile-base font-semibold text-gray-900 dark:text-white">
                Upcoming Interviews
              </h2>
              <button
                className="touch-target text-primary text-mobile-sm font-medium hover:text-primary-dark transition-colors"
                onClick={() => onViewSchedule?.()}
              >
                View All
              </button>
            </div>
          </div>
          
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {upcomingInterviews.map((interview) => (
              <div key={interview.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-mobile-sm font-medium text-gray-900 dark:text-white truncate">
                      {interview.candidate}
                    </p>
                    <p className="text-mobile-xs text-gray-600 dark:text-gray-400 truncate">
                      {interview.position}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-mobile-xs text-gray-500 dark:text-gray-400">
                        {interview.time} • {interview.duration}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-mobile-base font-semibold text-gray-900 dark:text-white">
              Recent Activities
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {recentActivities.map((activity) => {
              const Icon = getActivityIcon(activity.type);
              const colorClasses = getActivityColorClasses(activity.type, activity.status);
              
              return (
                <div key={activity.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${colorClasses}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-mobile-sm font-medium text-gray-900 dark:text-white truncate">
                        {activity.title}
                      </p>
                      <p className="text-mobile-xs text-gray-600 dark:text-gray-400 truncate">
                        {activity.subtitle}
                      </p>
                      <p className="text-mobile-xs text-gray-500 dark:text-gray-400 mt-1">
                        {activity.time}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom padding for mobile navigation */}
      <div className="h-20" />
    </div>
  );
};

export default React.memo(DashboardMobile);
